import { HTMLControl } from '../controls/html_control.js';
import { Trips } from '../data/trips.js';
import { window } from '../window.js';
import { ActivityListControl } from './activity_list_control.js';

import '../components/goingwith_control.js';
import '../controls/date_control.js';
import '../controls/number_control.js';
import './location_control.js';

/**
 * Checklist options controls such as activities, start date, location etc.
 */
class TripOptions extends HTMLControl {
  static get selectors() {
    const ActivityList = 'activity-list-control';
    const Location = 'location-control';
    return {
      AllActivityListItems: `${ActivityList} ${ActivityListControl.selectors.AllItems}`,
      ActivityList,
      Breaker: '.flex-breaker',
      StartDate: 'date-control',
      Duration: '.duration',
      GoingWith: 'goingwith-control',
      GroupSize: '.group-size',
      Location,
    };
  }

  static get elements() {
    let s = this.selectors;
    return {
      activityListElement: s.ActivityList,
      breakerElement: s.Breaker,
      durationElement: s.Duration,
      goingWithElement: s.GoingWith,
      groupSizeElement: s.GroupSize,
      locationElement: s.Location,
      startDateElement: s.StartDate,
    };
  }

  initialize() {
    super.initialize();

    this.classList.add('trip-options-control');

    // Start date control
    this.startDateElement.addEventListener('valuechange', ev =>
      this.#dispatchOptionChange('startDate', ev.detail)
    );

    // Duration control
    this.durationElement.errorMessage = `Please enter estimated number of days of the trip.
       It is used to generate a checklist and show weather.`;
    this.durationElement.onchange = () =>
      this.#dispatchOptionChange('duration', this.durationElement.value);

    // Location control
    this.locationElement.onchange = () =>
      this.#dispatchOptionChange('loc', this.locationElement.value);

    // Group size control.
    this.groupSizeElement.errorMessage = `Please enter the group size. It's helpful to generate a better list.`;
    this.groupSizeElement.onchange = () =>
      this.#dispatchOptionChange('groupSize', this.groupSizeElement.value);

    // Activities control
    this.activityListElement.onchange = () =>
      this.#dispatchOptionChange('activities', this.activityListElement.value);

    // Going-with control
    this.goingWithElement.invitesUpdated = data => this.invitesUpdated(data);
    this.goingWithElement.type = this.getAttribute('going-with-type');
  }

  static get markup() {
    return `
<date-control label='Start date'
              value='${Trips.weekendDateStr()}'></date-control>
<number-control class='duration'
                label='Number of days'
                value='2'></number-control>
<location-control label='Where'></location-control>
<number-control class='group-size'
                label='Group of'
                value='2'></number-control>
<span class='flex-breaker'></span>
<activity-list-control></activity-list-control>
<goingwith-control></goingwith-control>
`;
  }

  static properties = {
    friends: {
      set(v) {
        this.goingWithElement.friends = v;
      },
    },
    noLocation: {
      set(v) {
        this.locationElement.hidden = v;
      },
    },
    plainStyle: {
      set(v) {
        return this.#setPlainStyle(v);
      },
    },
  };

  get activities() {
    return this.activityListElement.value;
  }
  set activities(v) {
    this.activityListElement.value = v;
  }

  get startDate() {
    return this.startDateElement.value;
  }
  get dates() {
    let s = new Date(this.startDateElement.value);
    let e = new Date(s).setDate(s.getDate() + this.duration);
    return [s.valueOf(), e.valueOf()];
  }

  get duration() {
    return parseInt(this.durationElement.value);
  }

  get groupSize() {
    return parseInt(this.groupSizeElement.value);
  }
  set groupSize(v) {
    this.groupSizeElement.value = v;
  }

  get loc() {
    return this.location;
  }
  get location() {
    return this.locationElement.value;
  }
  set location(v) {
    this.locationElement.value = v;
  }

  get lat() {
    return this.locationElement.lat;
  }
  get lng() {
    return this.locationElement.lng;
  }

  get cocampers() {
    return this.goingWithElement.value;
  }
  set cocampers(v) {
    this.goingWithElement.value = v;
  }

  get consumerId() {
    return this.locationElement.consumerId;
  }

  set(data) {
    this.activityListElement.value = data.activities;
    this.startDateElement.value = data.startDate || null;
    this.durationElement.value = data.duration;
    this.groupSizeElement.value = data.groupSize;
    this.locationElement.value = data.loc;
    this.goingWithElement.value = data.cocampers;
  }

  setLocationTo(v) {
    this.locationElement.setLocationTo(v);
  }

  get readonly() {
    return this.activityListElement.readonly;
  }
  set readonly(v) {
    this.activityListElement.readonly = v;
    this.startDateElement.readonly = v;
    this.durationElement.readonly = v;
    this.groupSizeElement.readonly = v;
    this.locationElement.readonly = v;
    this.goingWithElement.readonly = v;
  }

  checkValidity() {
    return (
      this.startDateElement.checkValidity() &&
      this.durationElement.checkValidity() &&
      this.locationElement.checkValidity() &&
      this.groupSizeElement.checkValidity() &&
      this.activityListElement.checkValidity()
    );
  }

  validate() {
    return (
      this.startDateElement.validate() &&
      this.durationElement.validate() &&
      this.locationElement.validate() &&
      this.groupSizeElement.validate() &&
      this.activityListElement.validate()
    );
  }

  showMap() {
    this.locationElement.showMap();
  }

  #dispatchOptionChange(name, value) {
    this.dispatchEvent(
      new CustomEvent('optionchange', {
        detail: {
          name,
          value,
        },
      })
    );
  }

  #setPlainStyle(value) {
    this.breakerElement.hidden = !value;
    this.startDateElement.plainStyle = value;
    this.durationElement.plainStyle = value;
    this.locationElement.plainStyle = value;
    this.groupSizeElement.plainStyle = value;
    this.activityListElement.plainStyle = value;
    this.goingWithElement.plainStyle = value;
  }
}

window.customElements.define('trip-options', TripOptions);

export { TripOptions };
